import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/auth-context';
import {
  fetchSatoshiAccount,
  SatoshiAccountNotification,
  SatoshiAccountProfile,
} from '../services/satoshiAccount';
import { isRecord, normalizeArrayField } from '../utils/backendData';
import { applyVanerixBranding } from '../utils/branding';

type SatoshiAccountState = {
  loading: boolean;
  error: string | null;
  warnings: string[];
  users: SatoshiAccountProfile[];
  notifications: SatoshiAccountNotification[];
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

function isSatoshiAccountProfile(value: unknown): value is SatoshiAccountProfile {
  return isRecord(value);
}

function isSatoshiAccountNotification(value: unknown): value is SatoshiAccountNotification {
  return isRecord(value);
}

export default function SatoshiAccount() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { tg_chat_id } = useParams();
  const location = useLocation();
  const accountState = location.state as { accountName?: string } | undefined;
  const accountLabel = applyVanerixBranding(
    accountState?.accountName ? `Vanerix Account: ${accountState.accountName}` : 'Vanerix Account'
  );
  const [state, setState] = useState<SatoshiAccountState>({
    loading: true,
    error: null,
    warnings: [],
    users: [],
    notifications: [],
  });

  const load = async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetchSatoshiAccount(token, tg_chat_id);
      console.log('Vanerix account response:', data);

      if (!data.success) {
        throw new Error(applyVanerixBranding(data.message ?? 'Vanerix account request failed.'));
      }

      const warnings: string[] = [];
      const users = normalizeArrayField<unknown>(data.data, 'profiles', warnings).filter(
        (user, index): user is SatoshiAccountProfile => {
          if (isSatoshiAccountProfile(user)) return true;
          warnings.push(`profiles item ${index + 1} has an invalid format and could not be displayed.`);
          return false;
        }
      );
      const notifications = normalizeArrayField<unknown>(
        data.notifications,
        'notifications',
        warnings
      ).filter((notification, index): notification is SatoshiAccountNotification => {
        if (isSatoshiAccountNotification(notification)) return true;
        warnings.push(`notifications item ${index + 1} has an invalid format and could not be displayed.`);
        return false;
      });

      setState((prev) => ({
        ...prev,
        loading: false,
        error: null,
        warnings: warnings.map(applyVanerixBranding),
        users,
        notifications,
      }));
    } catch (err) {
      const msg = applyVanerixBranding(
        err instanceof Error ? err.message : 'Failed to load Vanerix account.'
      );
      setState((prev) => ({ ...prev, loading: false, error: msg, warnings: [], notifications: [] }));
    }
  };

  useEffect(() => {
    void load();
  }, [token, tg_chat_id]);

  return (
    <Box
      component="main"
      sx={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        bgcolor: 'background.default',
        p: 2,
        pt: 4,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 1100, width: '100%', borderRadius: '15px' }}>
        <Stack spacing={2}>
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                px: 4,
                py: 0.25,
                minWidth: 140,
                color: 'grey.800',
                borderColor: 'grey.300',
                bgcolor: 'grey.100',
                boxShadow: '0 6px 14px rgba(17, 24, 39, 0.08)',
                '&:hover': {
                  bgcolor: 'grey.200',
                  borderColor: 'grey.400',
                },
              }}
            >
              BACK
            </Button>
          </Box>
          <Stack direction="row" justifyContent="center">
            <Tooltip title="Profiles linked to this chat ID" arrow>
              <Chip
                label={accountLabel}
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  fontSize: 16,
                  bgcolor: 'grey.200',
                  color: 'grey.700',
                  borderRadius: '999px',
                  px: 1.5,
                  py: 2,
                  boxShadow: '0 8px 18px rgba(17, 24, 39, 0.15)',
                }}
              />
            </Tooltip>
          </Stack>

          {state.loading ? (
            <Stack alignItems="center" spacing={1}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading Vanerix account...
              </Typography>
            </Stack>
          ) : null}

          {state.error ? <Alert severity="error">{state.error}</Alert> : null}
          {state.warnings.length > 0 ? (
            <Alert severity="warning">
              {state.warnings.map((warning, index) => (
                <Typography key={`${warning}-${index}`} component="div" variant="body2">
                  {`- ${warning}`}
                </Typography>
              ))}
            </Alert>
          ) : null}

          {!state.loading && state.users.length > 0 ? (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                width: '100%',
                borderRadius: '15px',
                overflow: 'hidden',
                borderColor: 'grey.200',
                boxShadow: '0 8px 20px rgba(17, 24, 39, 0.08)',
              }}
            >
              <Table size="small" aria-label="Vanerix users">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: 'grey.200',
                      background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                    >
                      Profile Name
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                    >
                      Exchange Accounts
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.users.map((user, index) => (
                    <TableRow
                      key={user._id ?? `${user.name ?? 'profile'}-${index}`}
                      hover
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        navigate(`/satoshi-profile/${user._id}`, {
                          state: {
                            exchangeClients: user.exchangeClients ?? [],
                            accountName: accountState?.accountName,
                            profileName: user.name,
                          },
                        })
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          navigate(`/satoshi-profile/${user._id}`, {
                            state: {
                              exchangeClients: user.exchangeClients ?? [],
                              accountName: accountState?.accountName,
                              profileName: user.name,
                            },
                          });
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 150ms ease, transform 150ms ease',
                        '&:hover': { bgcolor: 'grey.50' },
                      }}
                    >
                      <TableCell align="center" sx={{ fontSize: 16, color: 'grey.900' }}>
                        {user.name ?? '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: 16, color: 'grey.900' }}>
                        {Array.isArray(user.exchangeClients) ? user.exchangeClients.length : 0}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}

          {!state.loading && state.notifications.length > 0 ? (
            <Stack>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  width: '100%',
                  borderRadius: '15px',
                  overflow: 'hidden',
                  borderColor: 'grey.200',
                  boxShadow: '0 8px 20px rgba(17, 24, 39, 0.08)',
                }}
              >
                <Table size="small" aria-label="telegram notifications">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: 'grey.200',
                        background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
                      }}
                    >
                      <TableCell
                        align="center"
                        colSpan={2}
                        sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                      >
                        Telegram Notifications
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.notifications.map((notification, index) => (
                      <TableRow
                        key={notification._id ?? `${notification.time}-${index}`}
                        hover
                        sx={{
                          transition: 'background-color 150ms ease, transform 150ms ease',
                          '&:hover': { bgcolor: 'grey.50' },
                        }}
                      >
                        <TableCell align="center" sx={{ fontSize: 16, color: 'grey.900', whiteSpace: 'nowrap' }}>
                          {formatDate(notification.time)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontSize: 16, color: 'grey.900', whiteSpace: 'pre-line' }}
                        >
                          {notification.info ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          ) : null}

          {!state.loading && state.users.length === 0 && !state.error ? (
            <Alert severity="info">No users found.</Alert>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}
